/**
 * PredictionModelScreen Component
 *
 * Main container screen for viewing prediction accuracy and understanding
 * the calibration system. Shows:
 * - Header with title and back button
 * - Strain selector dropdown
 * - Summary stats cards (MAPE, Bias, Calibration Impact)
 * - Prediction chart
 * - Variance breakdown section
 *
 * US-FORECAST-CALIBRATION Step 8
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Loader2, AlertCircle, TrendingUp } from 'lucide-react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { supabase } from '@/supabase-client'
import { usePredictionAccuracy } from '@/hooks/usePredictionAccuracy'
import { getPredictionTrend, getTopVarianceContributors } from '@/services/predictionAnalytics'
import PredictionChart from './PredictionChart'
import VarianceBreakdown from './VarianceBreakdown'

/**
 * Hook to fetch strains for the selector
 */
function useStrains() {
  const [strains, setStrains] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchStrains() {
      try {
        // Use strain families — demand_forecasts uses default_strain_id as strain_id
        const { data, error } = await supabase
          .from('strain_families')
          .select('id, name, default_strain_id, active')
          .eq('active', true)
          .order('name')

        if (error) throw error

        // Only include families that have forecast data
        const strainIds = (data || []).map(f => f.default_strain_id)
        const { data: forecasts } = await supabase
          .from('demand_forecasts')
          .select('strain_id')
          .in('strain_id', strainIds)
          .eq('is_actual', true)
        const strainsWithData = new Set((forecasts || []).map(f => f.strain_id))

        setStrains(
          (data || [])
            .filter(f => strainsWithData.has(f.default_strain_id))
            .map(f => ({ id: f.default_strain_id, name: f.name }))
        )
      } catch (err) {
        console.error('[PredictionModelScreen] Error fetching strains:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStrains()
  }, [])

  return { strains, loading }
}

/**
 * Hook to fetch chart trend data
 */
function usePredictionTrend(strainId, weeks = 12) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchTrend() {
      if (!strainId) {
        setData([])
        return
      }

      setLoading(true)
      setError(null)

      try {
        const trend = await getPredictionTrend(strainId, weeks)
        setData(trend)
      } catch (err) {
        console.error('[usePredictionTrend] Error:', err)
        setError(err.message)
        setData([])
      } finally {
        setLoading(false)
      }
    }

    fetchTrend()
  }, [strainId, weeks])

  return { data, loading, error }
}

/**
 * Hook to fetch variance contributors
 */
function useVarianceContributors(strainId, limit = 5) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    async function fetchVariance() {
      if (!strainId) {
        setData([])
        return
      }

      setLoading(true)

      try {
        const contributors = await getTopVarianceContributors(strainId, limit)
        setData(contributors)
      } catch (err) {
        console.error('[useVarianceContributors] Error:', err)
        setData([])
      } finally {
        setLoading(false)
      }
    }

    fetchVariance()
  }, [strainId, limit])

  return { data, loading }
}

/**
 * PredictionModelScreen - Main prediction analytics page
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function PredictionModelScreen({ user, onLogout }) {
  const navigate = useNavigate()

  // State
  const [selectedStrainId, setSelectedStrainId] = useState(null)
  const [weeks] = useState(12)

  // Data hooks
  const { strains, loading: strainsLoading } = useStrains()
  const {
    data: accuracyData,
    loading: accuracyLoading,
    error: accuracyError,
    refetch: refetchAccuracy
  } = usePredictionAccuracy({ strainId: selectedStrainId, weeks })

  const { data: trendData, loading: trendLoading, error: trendError } = usePredictionTrend(selectedStrainId, weeks)
  const { data: varianceData, loading: varianceLoading } = useVarianceContributors(selectedStrainId, 5)

  // Auto-select first strain when loaded
  useEffect(() => {
    if (strains.length > 0 && !selectedStrainId) {
      setSelectedStrainId(strains[0].id)
    }
  }, [strains, selectedStrainId])

  // Get current strain metrics
  const currentMetrics = accuracyData?.find(a => a.strain_id === selectedStrainId) || null
  const isLoading = strainsLoading || accuracyLoading || trendLoading

  // Handle back navigation
  const handleBack = () => {
    navigate('/sales/timeline')
  }

  // Handle refresh
  const handleRefresh = () => {
    refetchAccuracy()
  }

  // Render error state
  if (accuracyError || trendError) {
    return (
      <PageLayout
        title="Prediction Model"
        user={user}
        onLogout={onLogout}
      >
        <Alert variant="destructive" className="max-w-lg mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading data</AlertTitle>
          <AlertDescription className="mt-2">
            {accuracyError || trendError}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="mt-4 w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Prediction Model"
      subtitle="Forecast accuracy analysis and calibration metrics"
      user={user}
      onLogout={onLogout}
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Timeline
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Strain Selector */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-slate-700">
            Strain:
          </label>
          <Select
            value={selectedStrainId || ''}
            onValueChange={setSelectedStrainId}
            disabled={strainsLoading}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select strain" />
            </SelectTrigger>
            <SelectContent>
              {strains.map((strain) => (
                <SelectItem key={strain.id} value={strain.id}>
                  {strain.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isLoading && (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          )}
        </div>

        {/* No strain selected state */}
        {!selectedStrainId && !strainsLoading && (
          <div className="py-12 text-center">
            <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Select a strain to view prediction metrics</p>
          </div>
        )}

        {/* Main content */}
        {selectedStrainId && (
          <>
            {/* Prediction Chart */}
            <PredictionChart
              data={trendData}
              height={350}
            />

            {/* Variance Breakdown */}
            <VarianceBreakdown
              customers={varianceData}
            />

            {/* No data notice */}
            {!isLoading && (!trendData || trendData.length === 0) && (
              <Alert className="max-w-lg mx-auto">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No prediction data</AlertTitle>
                <AlertDescription>
                  No prediction snapshots found for this strain. Predictions will be
                  recorded automatically when sales suggestions are generated.
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
    </PageLayout>
  )
}
